import { useState, useEffect, useCallback } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { PopupModal } from "react-calendly";
import LogoIcon from "./LogoIcon";
import { useLanguage } from "../i18n/LanguageContext";
import { useTheme } from "../i18n/ThemeContext";

const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL;

const SunIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);
const MoonIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

export default function Navbar() {
  const { lang, toggle, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  const updateActiveSection = useCallback(() => {
    if (!isHome) return;
    const sectionIds = t.nav.links.map((l) => l.id);
    let current = "";
    for (const id of sectionIds) {
      const el = document.getElementById(id);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 200) current = id;
      }
    }
    setActiveSection(current);
  }, [isHome, t.nav.links]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);
      updateActiveSection();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [updateActiveSection]);

  const scrollTo = (id) => {
    setMobileOpen(false);
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = `/#${id}`;
    }
  };

  return (
    <>
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0F1A2E]/95 backdrop-blur-md border-b border-slate-700/60 py-3" : "bg-transparent py-5"
      }`}
    >
      {/* Scroll progress bar */}
      <div className="absolute top-0 left-0 h-[2px] bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] transition-none" style={{ width: `${scrollProgress}%` }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <LogoIcon className={`transition-all duration-500 ${scrolled ? "h-7" : "h-9"}`} />
          <span className="text-white font-semibold tracking-widest text-sm uppercase group-hover:text-[#D4AF37] transition-colors duration-300">
            Advaltis
          </span>
        </Link>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-8">
          {t.nav.links.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className={`text-[11px] uppercase tracking-widest transition-colors duration-200 relative ${
                isHome && activeSection === link.id
                  ? "text-[#D4AF37]"
                  : "text-slate-400 hover:text-[#D4AF37]"
              }`}
            >
              {link.label}
              {isHome && activeSection === link.id && (
                <span className="absolute -bottom-1 left-0 right-0 h-px bg-[#D4AF37] transition-all duration-300" />
              )}
            </button>
          ))}
          <Link
            to="/blog"
            data-testid="nav-blog-link"
            className={`text-[11px] uppercase tracking-widest hover:text-[#D4AF37] transition-colors duration-200 ${
              location.pathname.startsWith("/blog") ? "text-[#D4AF37]" : "text-slate-400"
            }`}
          >
            Blog
          </Link>
        </div>

        {/* Right side: lang toggle + CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            className="border border-slate-700 hover:border-[#D4AF37] text-slate-400 hover:text-[#D4AF37] p-2 transition-all duration-200"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <SunIcon size={13} /> : <MoonIcon size={13} />}
          </button>
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="border border-slate-700 hover:border-[#D4AF37] text-slate-400 hover:text-[#D4AF37] text-[11px] uppercase tracking-widest px-3 py-1.5 transition-all duration-200"
          >
            {lang === "fr" ? "EN" : "FR"}
          </button>
          <button
            data-testid="nav-book-call"
            onClick={() => setCalendlyOpen(true)}
            className="btn-gold bg-[#D4AF37] text-[#0B1221] px-5 py-2.5 text-[11px] font-bold uppercase tracking-wider hover:bg-[#F59E0B]"
          >
            {t.nav.bookCall}
          </button>
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden text-white min-h-11 min-w-11 flex items-center justify-center"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#0F1A2E]/98 backdrop-blur-lg border-t border-slate-700/60 py-6 px-6 space-y-4 mobile-menu-enter overflow-hidden">
          {t.nav.links.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className="block text-slate-300 text-sm uppercase tracking-widest hover:text-[#D4AF37] transition-colors py-2"
            >
              {link.label}
            </button>
          ))}
          <Link
            to="/blog"
            onClick={() => setMobileOpen(false)}
            data-testid="nav-blog-link-mobile"
            className={`block text-sm uppercase tracking-widest hover:text-[#D4AF37] transition-colors py-2 ${
              location.pathname.startsWith("/blog") ? "text-[#D4AF37]" : "text-slate-300"
            }`}
          >
            Blog
          </Link>
          <div className="flex items-center gap-3 pt-3">
            <button
              onClick={toggleTheme}
              className="border border-slate-700 text-slate-400 p-2.5 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <SunIcon size={14} /> : <MoonIcon size={14} />}
            </button>
            <button
              onClick={toggle}
              className="border border-slate-700 text-slate-400 text-sm uppercase tracking-widest px-3 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
            >
              {lang === "fr" ? "EN" : "FR"}
            </button>
            <button
              onClick={() => { setCalendlyOpen(true); setMobileOpen(false); }}
              className="btn-gold bg-[#D4AF37] text-[#0B1221] px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-[#F59E0B] flex-1"
            >
              {t.nav.bookCall}
            </button>
          </div>
        </div>
      )}
    </nav>

    <PopupModal
      url={CALENDLY_URL}
      open={calendlyOpen}
      onModalClose={() => setCalendlyOpen(false)}
      rootElement={document.getElementById("root")}
    />
    </>
  );
}
